import { pushToDataLayer } from '@almbrand/trackinghooks';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import { downloadBlob, getFileExtension, previewPolicyById } from 'common/DocumentService';
import { useState } from 'react';

const usePolicyPreview = () => {
	const { isMobile, isTablet } = useDeviceQuery();
	const [openModal, setOpenModal] = useState(false);
	const [error, setError] = useState(false);
	const [previewDocument, setPreviewDocument] = useState<PreviewDocument>({
		data: '',
		blobData: null,
		displayAsPdf: false,
		name: '',
		contentType: '',
	});

	// Generic opener for already-fetched blobs (e.g. policy overview PDF)
	// Will download directly on mobile/tablet, otherwise open modal preview
	const openBlobPreview = async (options: {
		blob: Blob;
		name: string;
		contentType?: string;
		displayAsPdf?: boolean; // allow forcing PDF viewer
		downloadTrackingData?: Record<string, unknown>; // optional tracking payload
		closeLabel?: string;
		downloadLabel?: string;
	}) => {
		const {
			blob,
			name,
			contentType = blob.type || 'application/octet-stream',
			displayAsPdf = contentType === 'application/pdf',
			downloadTrackingData,
			closeLabel,
			downloadLabel,
		} = options;

		if (downloadTrackingData) pushToDataLayer(downloadTrackingData);

		if (isMobile || isTablet) {
			// Fallback to direct download on small devices
			downloadBlob(blob, name, getFileExtension(contentType));
			return;
		}

		// Create object URL for PDF or attempt to read as text for non-PDF
		let dataString = '';
		if (displayAsPdf) {
			dataString = URL.createObjectURL(blob);
		} else {
			try {
				dataString = await blob.text();
			} catch (e) {
				dataString = '';
			}
		}

		await setPreviewDocument({
			data: dataString,
			blobData: blob,
			displayAsPdf,
			name,
			contentType,
			closeLabel,
			downloadLabel,
		});
		await setOpenModal(true);
	};

	const handlePolicyClick = async (
		policyNumber: string,
		title: string,
		downloadTrackingData?: Record<string, unknown>
	) => {
		try {
			const response = await previewPolicyById(policyNumber);

			if (downloadTrackingData) pushToDataLayer(downloadTrackingData);

			if (isMobile || isTablet) {
				const blob = new Blob([response.blobData], { type: response.contentType });
				downloadBlob(blob, title, getFileExtension(response.contentType));
				return;
			}

			await setPreviewDocument({
				data: response.serializedData,
				blobData: response.blobData,
				displayAsPdf: response.displayAsPdf,
				name: title,
				contentType: response.contentType || '',
				closeLabel: 'Luk police',
				downloadLabel: 'Download police',
			});

			await setOpenModal(true);
		} catch (error) {
			console.error('Error fetching policy:', error);
			setError(true);
		}
	};

	const handlePolicyDownload = async () => {
		const blob = new Blob([previewDocument.blobData], { type: previewDocument.contentType });
		downloadBlob(blob, previewDocument.name, getFileExtension(previewDocument.contentType), previewDocument.date);
	};
	const onModalClose = () => setOpenModal(false);
	const resetError = () => setError(false);

	return {
		openModal,
		previewDocument,
		error,
		handlePolicyClick,
		handlePolicyDownload,
		openBlobPreview,
		onModalClose,
		resetError,
	};
};

export default usePolicyPreview;
